import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { I18nextProvider, useTranslation as useI18nextTranslation } from "react-i18next";
import { useEffect } from "react";
import en from "../locales/en/translation.json";
import fr from "../locales/fr/translation.json";
import ptPT from "../locales/pt-PT/translation.json";
import sourceKeys from "./sourceKeys.json";

export const LANGUAGE_STORAGE_KEY = "luali-language";
const SENTENCE_CASE_PROPER_WORDS = new Map([
  ["luali", "Luali"], ["google", "Google"], ["jebel", "Jebel"], ["ali", "Ali"],
  ["dubai", "Dubai"], ["quimera", "Quimera"],
]);
const SENTENCE_CASE_COUNTRY_WORDS = new Map([
  ["united", "United"], ["kingdom", "Kingdom"], ["states", "States"],
  ["royaume", "Royaume"], ["uni", "Uni"], ["etats", String.fromCharCode(201) + "tats"], ["unis", "Unis"],
  ["reino", "Reino"], ["unido", "Unido"], ["estados", "Estados"], ["unidos", "Unidos"],
]);
const SENTENCE_CASE_COUNTRY_PAIRS = [
  ["united", "kingdom"], ["united", "states"], ["royaume", "uni"], ["etats", "unis"],
  ["reino", "unido"], ["estados", "unidos"],
];
const SENTENCE_CASE_ACRONYMS = new Map([
  ["id", "ID"], ["otp", "OTP"], ["jpg", "JPG"], ["png", "PNG"], ["webp", "WEBP"],
  ["pdf", "PDF"], ["mb", "MB"], ["api", "API"], ["url", "URL"],
  ["ai", "AI"], ["vat", "VAT"], ["rfq", "RFQ"],
]);
const SENTENCE_CASE_WORD_PATTERN = new RegExp(
  "[A-Za-z0-9" + String.fromCharCode(192) + "-" + String.fromCharCode(591) + "]+",
);
const SENTENCE_CASE_MARK_PATTERN = new RegExp(
  "[" + String.fromCharCode(92) + "u0300-" + String.fromCharCode(92) + "u036f]",
  "g",
);

export const sentenceCaseLabel = (value) => {
  if (value === null || value === undefined) return value;
  const words = String(value).trim().replace(/[_-]+/g, " ").split(" ").filter(Boolean);
  const cores = words.map((word) => {
    const core = word.match(SENTENCE_CASE_WORD_PATTERN)?.[0] || "";
    return core.normalize("NFD").replace(SENTENCE_CASE_MARK_PATTERN, "").toLowerCase();
  });
  return words.map((word, index) => {
    const core = word.match(SENTENCE_CASE_WORD_PATTERN)?.[0];
    if (!core) return word;
    const lowerCore = cores[index];
    const isCountryWord = SENTENCE_CASE_COUNTRY_PAIRS.some(([first, second]) =>
      (lowerCore === first && cores[index + 1] === second) ||
      (lowerCore === second && cores[index - 1] === first),
    );
    const isBrandWord = lowerCore === "commodities" && cores[index - 1] === "luali";
    const properName = SENTENCE_CASE_PROPER_WORDS.get(lowerCore);
    const properCountry = isCountryWord ? SENTENCE_CASE_COUNTRY_WORDS.get(lowerCore) : undefined;
    const properBrand = isBrandWord ? "Commodities" : undefined;
    const proper = properName || properCountry || properBrand;
    const acronym = SENTENCE_CASE_ACRONYMS.get(lowerCore);
    const replacement = proper
      ? (core.toLocaleLowerCase() === lowerCore ? proper : core)
      : acronym || core.toLocaleLowerCase();
    const formatted = word.replace(core, replacement);
    return index === 0 && !proper && !acronym
      ? formatted.replace(/^./u, (letter) => letter.toLocaleUpperCase())
      : formatted;
  }).join(" ");
};

export const i18n = i18next.use(LanguageDetector);

i18n.init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      "pt-PT": { translation: ptPT },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "fr", "pt-PT"],
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    keySeparator: false,
    ns: ["translation"],
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
});

const englishTextKeys = { ...sourceKeys };
Object.entries(en).forEach(([key, value]) => {
  if (typeof value === "string") englishTextKeys[value.trim()] ||= key;
});

const STATUS_KEYS = {
  pending: "status.pending", accepted: "status.accepted", rejected: "status.rejected", reject: "status.rejected",
  other: "status.other", cancelled: "status.cancelled", canceled: "status.cancelled",
  completed: "status.completed", complete: "status.completed", delayed: "status.delayed",
  in_transit: "status.inTransit", transit: "status.inTransit", order_created: "status.orderCreated",
  order_placed: "status.orderPlaced", delivered: "status.delivered", initiated: "status.initiated",
  current: "status.current", approved: "status.approved", verified: "status.verified",
  pending_review: "status.pendingReview", expiring_soon: "status.expiringSoon", active: "status.active",
  inactive: "status.inactive", draft: "status.draft", new: "status.new", submitted: "status.submitted",
  paid: "status.paid", failed: "status.failed", processing: "status.processing", unknown: "status.unknown",
};

export function translateText(source, variables = {}) {
  if (typeof source !== "string") return source;
  const key = englishTextKeys[source.trim()];
  return key ? i18next.t(key, variables) : source;
}

export function translateStatus(value) {
  if (value === null || value === undefined || value === "") return value;
  const raw = String(value);
  const normalized = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const key = STATUS_KEYS[normalized];
  return sentenceCaseLabel(key && i18next.exists(key) ? i18next.t(key) : raw);
}

export function I18nProvider({ children }) {
  useEffect(() => {
    const updateDocumentLanguage = (language) => {
      if (typeof document !== "undefined") document.documentElement.lang = language;
    };
    updateDocumentLanguage(i18next.resolvedLanguage || i18next.language || "en");
    i18next.on("languageChanged", updateDocumentLanguage);
    return () => i18next.off("languageChanged", updateDocumentLanguage);
  }, []);
  return <I18nextProvider i18n={i18next}>{children}</I18nextProvider>;
}

export function useTranslation() {
  const { t: i18nT, i18n: instance } = useI18nextTranslation();
  const language = instance.resolvedLanguage || instance.language || "en";
  const t = (key, variables) => i18nT(key, variables);
  const text = (source, variables) => {
    if (typeof source !== "string") return source;
    const key = englishTextKeys[source.trim()];
    return key ? i18nT(key, variables) : source;
  };
  const status = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const raw = String(value);
    const normalized = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
    const key = STATUS_KEYS[normalized];
    return key && instance.exists(key) ? i18nT(key) : raw;
  };
  return { language, setLanguage: (nextLanguage) => instance.changeLanguage(nextLanguage), t, text, status };
}
